/**
 * Centralized fuel calculation utilities
 * Single source of truth for all fuel cost and reward calculations
 */

/**
 * Input data for fuel expense calculations
 */
export interface FuelExpenseData {
  amount: number;              // Gross cost in cents
  gallons?: number;            // Gallons purchased
  pricePerGallon?: number;     // Raw price in cents per gallon
  rewardsPercent?: number;     // Flex Rewards percentage (e.g., 10 for 10%)
  upsidePerGallon?: number;    // Upside cash back in cents per gallon
}

/**
 * Result of fuel expense calculations
 */
export interface FuelCalculationResult {
  grossCost: number;                    // Original amount in cents
  flexReward: number;                   // Flex Rewards amount in cents
  upsideReward: number;                 // Upside reward amount in cents
  totalReward: number;                  // Combined rewards in cents
  netCost: number;                      // Cost after rewards in cents
  effectivePricePerGallon: number | null; // Net cost / gallons in cents
}

/**
 * Calculate all fuel-related values from expense data
 * @param expense - Fuel expense data
 * @returns Complete calculation result
 */
export function calculateFuelExpense(expense: FuelExpenseData): FuelCalculationResult {
  const { amount, gallons, rewardsPercent, upsidePerGallon } = expense;

  // Flex Rewards: percentage of total amount
  const flexReward = rewardsPercent
    ? Math.round(amount * rewardsPercent / 100)
    : 0;

  // Upside: per-gallon cash back
  const upsideReward = gallons && upsidePerGallon
    ? Math.round(gallons * upsidePerGallon)
    : 0;

  const totalReward = flexReward + upsideReward;
  const netCost = amount - totalReward;

  // Effective price = net cost / gallons
  const effectivePricePerGallon = gallons && gallons > 0
    ? Math.round(netCost / gallons)
    : null;

  return {
    grossCost: amount,
    flexReward,
    upsideReward,
    totalReward,
    netCost,
    effectivePricePerGallon,
  };
}

/**
 * Calculate estimated fuel cost for a given distance
 * @param miles - Distance in miles
 * @param mpg - Miles per gallon (from settings)
 * @param pricePerGallon - Effective price per gallon in cents (after rewards)
 * @returns Fuel cost in cents
 */
export function calculateFuelCost(
  miles: number,
  mpg: number,
  pricePerGallon: number
): number {
  if (miles <= 0 || mpg <= 0 || pricePerGallon <= 0) return 0;
  const gallonsUsed = miles / mpg;
  return Math.round(gallonsUsed * pricePerGallon);
}

/**
 * Calculate net earnings after fuel cost
 * @param earnings - Total earnings in cents
 * @param fuelCost - Fuel cost in cents
 * @returns Net earnings in cents
 */
export function calculateNetEarnings(
  earnings: number,
  fuelCost: number
): number {
  return earnings - fuelCost;
}
